import { Button } from "src/Components/ui/button";
import { DialogClose } from "src/Components/ui/dialog";
import { Form, FormField } from "src/Components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import FormFieldItem from "src/common/form/FormFieldItem";
import { indexStore } from "src/store/indexStore";
import { z } from "zod";
import { Input } from "src/Components/ui/input";
import { SelectBoxForm } from "src/Components/selectbox/SelectBoxForm";
import { userTypes } from "src/enums/usersTpes";
import { InputPassword } from "src/Components/utils/InputPassword";
import { Label } from "src/Components/ui/label";
import { Switch } from "src/Components/ui/switch";
import { ComboboxMultiSelectForm } from "src/Components/selectbox/ComboboxMultiSelectForm";
import Visibility from "src/Components/utils/Visibility";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "src/Components/ui/card";
import { User, Phone, Lock, Shield, CheckCircle2, XCircle, Building2, Info } from "lucide-react";

function UserForm(props) {
  const { nameLang, branches, tenants } = indexStore();
  const { t } = useTranslation();
  const roles = [
    {
      id: userTypes.ADMIN,
      name: t(`userTypes.${userTypes.ADMIN}`),
    },
    {
      id: userTypes.DATAMANAGER,
      name: t(`userTypes.${userTypes.DATAMANAGER}`),
    },
    { id: userTypes.CALLCENTER, name: t(`userTypes.${userTypes.CALLCENTER}`) },
  ];
  const formSchema = z.object({
    name: z
      .string({
        required_error: `${t("gloabl.name")} ${t(
          "errorMessage.fieldRequired"
        )}.`,
      })
      .min(2, {
        message: `${t("gloabl.name")} ${t("errorMessage.fieldCharsLength")}.`,
      }),

    phone: z
      .string({
        required_error: `${t("gloabl.phone")} ${t(
          "errorMessage.fieldRequired"
        )}.`,
      })
      .min(2, {
        message: `${t("gloabl.phone")} ${t("errorMessage.fieldCharsLength")}.`,
      }),
    password: props.editMode
      ? z.string().optional() // If in edit mode, make the password optional
      : z
          .string({
            required_error: `${t("gloabl.Password")} ${t(
              "errorMessage.fieldRequired"
            )}.`,
          })
          .min(2, {
            message: `${t("gloabl.Password")} ${t(
              "errorMessage.fieldCharsLength"
            )}.`,
          }),

    role: z.string({
      required_error: `${t("gloabl.role")} ${t("errorMessage.fieldRequired")}.`,
    }),
    status: z.any({
      required_error: `${t("gloabl.active")} ${t(
        "errorMessage.fieldRequired"
      )}.`,
    }),
    branch_ids: z
      .array(z.any())
      .min(1, { message: "Select at least one branch." }),
  });

  const form = useForm({
    resolver: zodResolver(formSchema),

    defaultValues: {
      ...props.datasource,
    },
  });
  const dialogCloseRef = useRef();

  const onSubmit = (values) => {
    const submitValues = {
      ...values,
      status: values.status ? 1 : 0,
      branch_ids: values.role === userTypes.ADMIN ? [] : values.branch_ids,
    };

    props.onSubmit(submitValues);
    if (dialogCloseRef.current) {
      dialogCloseRef.current.click();
    }
  };

  const onErorr = (errors) => {
    toast.error(t("errorMessage.fillAll"));
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit, onErorr)}
        className="space-y-6"
      >
        {/* Basic Information */}
        <Card className="border-2">
          <CardHeader className="flex-row items-center gap-3 pb-3 border-b border-blue-200 dark:border-blue-900">
            <div className="p-2 rounded-lg bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
              <Info className="w-5 h-5" />
            </div>
            <div>
              <CardTitle className="text-lg">{t("gloabl.basic_info") || "Basic Information"}</CardTitle>
              <CardDescription>{t("gloabl.user_basic_info_desc") || "Enter user name and contact information"}</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormFieldItem
                  required
                  form={form}
                  label={
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span>{t("gloabl.name")}</span>
                    </div>
                  }
                >
                  <Input {...field} placeholder={t("gloabl.user_name_placeholder") || "Enter user name"} />
                </FormFieldItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormFieldItem
                  required
                  form={form}
                  label={
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      <span>{t("gloabl.phone")}</span>
                    </div>
                  }
                >
                  <Input {...field} type="tel" placeholder={t("gloabl.phone_placeholder") || "Enter phone number"} />
                </FormFieldItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormFieldItem
                  required={!props.datasource}
                  form={form}
                  label={
                    <div className="flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      <span>{t("gloabl.Password")}</span>
                    </div>
                  }
                  description={props.datasource ? t("gloabl.password_optional_edit") || "Leave empty to keep current password" : ""}
                >
                  <InputPassword
                    name={"password"}
                    control={form.control}
                    {...field}
                  />
                </FormFieldItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Role & Permissions */}
        <Card className="border-2">
          <CardHeader className="flex-row items-center gap-3 pb-3 border-b border-purple-200 dark:border-purple-900">
            <div className="p-2 rounded-lg bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <CardTitle className="text-lg">{t("gloabl.role_permissions") || "Role & Permissions"}</CardTitle>
              <CardDescription>{t("gloabl.role_permissions_desc") || "Set user role and access permissions"}</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormFieldItem
                  required
                  form={form}
                  label={
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      <span>{t("gloabl.role")}</span>
                    </div>
                  }
                  description={t("gloabl.select_user_role") || "Choose the user's role and permissions"}
                >
                  <SelectBoxForm
                    textValue
                    data={roles}
                    {...field}
                    placeholder={t("gloabl.select") + " " + t("gloabl.role")}
                    label={"name"}
                    valueKey={"id"}
                  />
                </FormFieldItem>
              )}
            />
            <Visibility
              visible={
                form.watch("role") === userTypes.CALLCENTER ||
                form.watch("role") === userTypes.DATAMANAGER
              }
            >
              <FormField
                control={form.control}
                name="branch_ids"
                render={({ field }) => (
                  <FormFieldItem
                    required
                    form={form}
                    label={
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        <span>{t("gloabl.branch")}</span>
                      </div>
                    }
                    description={t("gloabl.select_user_branches") || "Select branches this user can access"}
                  >
                    <ComboboxMultiSelectForm
                      {...field}
                      name="branch_ids"
                      control={form.control}
                      data={branches}
                      lable={nameLang}
                      placeholder={t("gloabl.select") + " " + t("gloabl.branch")}
                    />
                  </FormFieldItem>
                )}
              />
            </Visibility>
          </CardContent>
        </Card>

        {/* Status */}
        <Card className="border-2">
          <CardHeader className="flex-row items-center gap-3 pb-3 border-b border-gray-200 dark:border-gray-900">
            <div className="p-2 rounded-lg bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">
              {form.watch("status") ? (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600" />
              )}
            </div>
            <div>
              <CardTitle className="text-lg">{t("gloabl.status")}</CardTitle>
              <CardDescription>{t("gloabl.user_status_desc") || "Enable or disable user account"}</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormFieldItem form={form}>
                  <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/30">
                    <div className="flex items-center gap-3">
                      {field.value ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                      <div>
                        <Label className="text-sm font-medium cursor-pointer">
                          {t("gloabl.active")}
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {t("gloabl.user_status_desc") || "Enable or disable user account"}
                        </p>
                      </div>
                    </div>
                    <Switch
                      dir="ltr"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </div>
                </FormFieldItem>
              )}
            />
          </CardContent>
        </Card>

        <div className="flex items-center gap-3 justify-end">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t("gloabl.cancel")}
            </Button>
          </DialogClose>
          <DialogClose ref={dialogCloseRef} />
          <Button type="submit">{t("gloabl.save")}</Button>
        </div>
      </form>
    </Form>
  );
}

export default UserForm;
